import React, {useState} from 'react';
import PropTypes from 'prop-types';
import Color from '~/data/records/Color';
import {PLACEHOLDER_GREY} from '~/constants/InkConstants';
import {COLOR_SWATCH_HOVER_WARNING} from '~/constants/DesignSuggestionConstants';
import {isNamesOrNumbers} from '~/data/accessors/icon';
import {iconTypeToFriendlyName} from '~/utils/icon';
import {metrics} from '~/vendor/metrics';
import {ColorSwatch} from '@customink/pigment-react';

import './ColorSwatch.scss';

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
export default function NdxColorSwatch(props) {
  const {
    altColor,
    color = PLACEHOLDER_GREY,
    displayOnly = false,
    hoveredColorWithSubtletyWarning,
    isBookmarked = () => false,
    isSelected = () => false,
    isSubtle = () => false,
    onHoverOff = () => {},
    onHoverOn = () => {},
    onSwatchClick,
    sx
  } = props;
  const isBookmarkedResult = isBookmarked(color);
  const isSelectedResult = isSelected(color);
  const isSubtleResult = isSubtle(color);
  const clickable = !!props.onSwatchClick;
  const [isHovered, setHovered] = useState(false);

  const fireMetrics = () => {
    if (isSubtleResult && props.selectedIconType) {
      metrics.fireEvent(
        'subtlety in lab',
        'select subtle color',
        isNamesOrNumbers(props.selectedIconType)
          ? 'personalization'
          : iconTypeToFriendlyName(props.selectedIconType).toLowerCase()
      );
    }
  };

  const getTrim = () => {
    return color.secondary && color.secondary.hexString !== undefined
      ? `#${color.secondary.hexString}`
      : undefined;
  };

  return (
    <div className="ndx-ColorSwatch-wrapper">
      <ColorSwatch
        color={
          color?.hexString === '00000000' ? undefined : `#${color.hexString}`
        }
        trimRgb={getTrim()}
        dogEar={isBookmarkedResult}
        displayOnly={displayOnly}
        subtlety={isSubtleResult}
        selected={isSelectedResult}
        sx={sx}
        onClick={
          clickable
            ? evt => {
                onSwatchClick(evt, color, isBookmarkedResult, isSelectedResult);
                onHoverOff();
                fireMetrics();
              }
            : undefined
        }
        onMouseEnter={() => {
          onHoverOn(color);
          setHovered(true);
        }}
        onMouseLeave={() => {
          onHoverOff();
          setHovered(false);
        }}
        value={this?.value ?? color}
      />
      {altColor && (
        <div
          className="ndx-ColorSwatch-wrapper-altColor"
          style={{backgroundColor: altColor.cssString}}
        />
      )}
      {isHovered && hoveredColorWithSubtletyWarning === color.id && (
        <div className="ndx-subtletyWarning">
          <div className="ndx-subtletyWarning-content">
            <div className="ndx-subtletyWarning-content-header">
              <div className="ndx-subtletyWarning-content-header-title">
                <div className="ndx-subtletyWarning-content-header-icon" />
                Low contrast color
              </div>
              <div className="ndx-subtletyWarning-content-body">
                {COLOR_SWATCH_HOVER_WARNING}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const propTypes = {
  altColor: PropTypes.instanceOf(Color),
  color: PropTypes.instanceOf(Color),
  displayOnly: PropTypes.bool,
  hoveredColorWithSubtletyWarning: PropTypes.number,
  isBookmarked: PropTypes.func,
  isSelected: PropTypes.func,
  isSmall: PropTypes.bool,
  isSubtle: PropTypes.func,
  selectedIconType: PropTypes.string,
  sx: PropTypes.any,
  value: PropTypes.any,
  onHoverOff: PropTypes.func,
  onHoverOn: PropTypes.func,
  onSwatchClick: PropTypes.func
};

NdxColorSwatch.propTypes = propTypes;
